// Shared intake-form primitives, used by both the Evaluate and Discover forms.

import type { Depth } from '../types.js';

export function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  // A <div>, not a <label>: several fields contain groups of checkboxes/radios, and
  // a <label> wrapping multiple controls toggles only its first one on any click.
  return (
    <div className="tbo-field">
      <span className="tbo-label">
        {label}
        {required && <span className="tbo-req"> *</span>}
      </span>
      {children}
    </div>
  );
}

export function Select({
  value,
  options,
  onChange,
}: {
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select…</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

const DEPTH_OPTIONS: Array<{ value: Depth; title: string; desc: string }> = [
  { value: 'fast', title: 'Fast', desc: 'One round of questions, then your result. ~1 minute.' },
  {
    value: 'deep',
    title: 'Deep',
    desc: 'The AI asks follow-up rounds until it has what it needs (up to 4 in total) for a sharper read.',
  },
];

export function DepthSelector({
  depth,
  onChange,
}: {
  depth: Depth;
  onChange: (d: Depth) => void;
}): React.JSX.Element {
  return (
    <fieldset>
      <legend>Analysis depth</legend>
      <div className="tbo-depth">
        {DEPTH_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className={`tbo-depth-card${depth === opt.value ? ' is-selected' : ''}`}
          >
            <input
              type="radio"
              name="depth"
              value={opt.value}
              checked={depth === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <span className="tbo-depth-title">{opt.title}</span>
            <span className="tbo-depth-desc">{opt.desc}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
